<template>
  <div class="scroll-container" ref="containerRef">
    <div class="content" ref="contentRef">
      <slot></slot>
    </div>
    <div class="scrollbar scrollbar-horizontal" ref="hScrollbar">
      <div class="scrollbar-thumb" ref="hThumb"></div>
    </div>
    <div class="scrollbar scrollbar-vertical" ref="vScrollbar">
      <div class="scrollbar-thumb" ref="vThumb"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from "vue";
import type { Ref } from "vue";

// Define refs for container and scrollbars
const containerRef: Ref<HTMLDivElement | null> = ref(null);
const contentRef: Ref<HTMLDivElement | null> = ref(null);
const hScrollbar: Ref<HTMLDivElement | null> = ref(null);
const vScrollbar: Ref<HTMLDivElement | null> = ref(null);
const hThumb: Ref<HTMLDivElement | null> = ref(null);
const vThumb: Ref<HTMLDivElement | null> = ref(null);

const onScroll = (): void => {
  updateScrollbars();
};

const updateScrollbars = (): void => {
  const container = containerRef.value;
  const content = contentRef.value;
  const hScrollThumb = hThumb.value;
  const vScrollThumb = vThumb.value;

  if (container && content && hScrollThumb && vScrollThumb) {
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const contentWidth = content.scrollWidth;
    const contentHeight = content.scrollHeight;

    // Update horizontal scrollbar thumb size and position
    hScrollThumb.style.width = `${(containerWidth / contentWidth) * 100}%`;
    hScrollThumb.style.transform = `translateX(${(container.scrollLeft / contentWidth) * 100}%)`;

    // Update vertical scrollbar thumb size and position
    vScrollThumb.style.height = `${(containerHeight / contentHeight) * 100}%`;
    vScrollThumb.style.transform = `translateY(${(container.scrollTop / contentHeight) * 100}%)`;
  }
};

onMounted(() => {
  updateScrollbars();
  window.addEventListener('resize', updateScrollbars);
});

watch(containerRef, updateScrollbars, { immediate: true });

const startDragging = (e: MouseEvent, axis: 'horizontal' | 'vertical'): void => {
  const startX = e.clientX;
  const startY = e.clientY;
  const container = containerRef.value;
  const scrollStartX = container ? container.scrollLeft : 0;
  const scrollStartY = container ? container.scrollTop : 0;

  const onMouseMove = (e: MouseEvent): void => {
    if (container) {
      if (axis === 'horizontal') {
        container.scrollLeft = scrollStartX + (e.clientX - startX) * (container.scrollWidth / container.clientWidth);
      } else {
        container.scrollTop = scrollStartY + (e.clientY - startY) * (container.scrollHeight / container.clientHeight);
      }
      updateScrollbars();
    }
  };

  const onMouseUp = (): void => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
};

const addDragHandlers = (): void => {
  const hScrollThumb = hThumb.value;
  const vScrollThumb = vThumb.value;

  if (hScrollThumb) {
    hScrollThumb.addEventListener('mousedown', (e: MouseEvent) => startDragging(e, 'horizontal'));
  }
  if (vScrollThumb) {
    vScrollThumb.addEventListener('mousedown', (e: MouseEvent) => startDragging(e, 'vertical'));
  }
};

onMounted(addDragHandlers);
</script>

<style scoped>
.scroll-container {
  position: relative;
  overflow: hidden;
}

.content {
  width: 100%;
  height: 100%;
  overflow: hidden;
  transform: translate(0, 0);
}

.scrollbar {
  position: absolute;
  background: rgba(0, 0, 0, 0.3);
}

.scrollbar-horizontal {
  height: 10px;
  bottom: 0;
  left: 0;
  right: 0;
}

.scrollbar-vertical {
  width: 10px;
  top: 0;
  bottom: 0;
  right: 0;
}

.scrollbar-thumb {
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  cursor: pointer;
}
</style>
